import type { AnchorHTMLAttributes, MouseEvent, ReactElement } from 'react'
import { useRef, useState } from 'react'

import { type ListItemProps } from '@material-ui/core/ListItem'
import { nanoid } from 'nanoid'

import { ListItemIconStyled } from './styles/ListItemIconStyled'
import { ListItemTextStyled } from './styles/ListItemTextStyled'
import { MenuListItemStyled } from './styles/MenuListItemStyled'
import { type IconElement } from '@ui/elements/Icon'
import { type IWithTooltipProps, WithTooltip } from '@ui/elements/Tooltip'
import { getAnalyticsDataAttrs } from '@ui/utils/analytics'

export interface IMenuListItemProps extends ListItemProps {
  icon?: IconElement
  text: string | ReactElement
  sticky?: boolean
  /**
   * Shown dimmed and ignores activation, but stays reachable by the arrow
   * keys (`aria-disabled`), so a `tooltip` explaining why can be read.
   */
  disabled?: boolean
  selected?: boolean
  selectedIcon?: IconElement
  closeOnSelect?: boolean
  centerText?: boolean
  /**
   * Shown on hover and keyboard focus, and always exposed as the item's
   * accessible description (the reason a disabled item is unavailable).
   */
  tooltip?: IWithTooltipProps['title']
  linkProps?: AnchorHTMLAttributes<HTMLAnchorElement>
  onClick?(e: MouseEvent<HTMLElement>): void
  /** Unique identifier for analytics tracking for menu list item interactions. */
  analyticsId?: string
}

export const MenuListItem = ({
  text,
  icon,
  selectedIcon,
  sticky = false,
  disabled = false,
  selected = false,
  closeOnSelect = null,
  centerText = false,
  tooltip,
  linkProps,
  onClick = () => undefined,
  analyticsId,
  tabIndex = -1,
  ...props
}: IMenuListItemProps) => {
  const [isSelected, setIsSelected] = useState(selected)
  const [isTooltipOpen, setIsTooltipOpen] = useState(false)
  const descriptionIdRef = useRef(`menu-list-item-${nanoid(10)}-description`)
  const analyticsProps = getAnalyticsDataAttrs(analyticsId)

  const handleMenuItemClick = (e: MouseEvent<HTMLElement>) => {
    if (disabled) {
      e.preventDefault()
      return
    }

    if (onClick) {
      onClick(e)
    }
    linkProps?.onClick?.(e as MouseEvent<HTMLAnchorElement>)
    setIsSelected(!isSelected)
  }

  const menuItem = (
    <MenuListItemStyled
      className='menu-list-item'
      role='menuitem'
      tabIndex={tabIndex}
      aria-disabled={disabled}
      descriptionId={tooltip ? descriptionIdRef.current : undefined}
      selected={isSelected}
      {...(linkProps && { component: 'a', ...linkProps })}
      onClick={handleMenuItemClick}
      aria-label={props['aria-label']}
      {...analyticsProps}
    >
      {icon && (
        <ListItemIconStyled>
          {isSelected && selectedIcon ? selectedIcon : icon}
        </ListItemIconStyled>
      )}

      <ListItemTextStyled centerText={centerText} inset={true} primary={text} />

      {tooltip && (
        <span id={descriptionIdRef.current} hidden>
          {tooltip}
        </span>
      )}
    </MenuListItemStyled>
  )

  // Always inside the Tooltip, so the item keeps its node, and focus, when
  // its tooltip comes or goes while the menu is open. `open` is controlled
  // because the DS Tooltip wraps an empty title in an element, which MUI
  // would still open.
  return (
    <WithTooltip
      title={tooltip ?? ''}
      placement='left'
      open={Boolean(tooltip) && isTooltipOpen}
      onOpen={() => setIsTooltipOpen(true)}
      onClose={() => setIsTooltipOpen(false)}
    >
      {menuItem}
    </WithTooltip>
  )
}
